import { Select as SelectPrimitive } from "@base-ui/react/select";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon } from "../icon";
import {
  MenuItemLayout,
  menuItemClassName,
  menuSurfaceClassName,
} from "../menu/menu-internals";
import type { SelectOption, SelectProps } from "./select";

type PlainSelectControlProps = Pick<
  SelectProps,
  | "id"
  | "options"
  | "disabled"
  | "readOnly"
  | "required"
  | "name"
  | "placeholder"
  | "label"
  | "data-testid"
  | "renderValue"
  | "renderOption"
  | "popupWidth"
> & {
  value: string | null;
  selectedOption: SelectOption | undefined;
  onValueChange: NonNullable<SelectProps["onChange"]>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClassName: string;
};

export function PlainSelectControl({
  id,
  options,
  value,
  selectedOption,
  onValueChange,
  open,
  onOpenChange,
  disabled,
  readOnly,
  required,
  name,
  placeholder,
  label,
  "data-testid": dataTestId,
  renderValue,
  renderOption,
  popupWidth,
  triggerClassName,
}: PlainSelectControlProps) {
  const portalHost = usePortalHost();

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      name={name}
      items={options}
      modal={false}
    >
      <SelectPrimitive.Trigger
        id={id}
        data-testid={dataTestId}
        className={triggerClassName}
        aria-label={
          label || id
            ? undefined
            : readOnly
              ? "Select option"
              : (placeholder ?? "Select option")
        }
        aria-required={required || undefined}
        aria-readonly={readOnly || undefined}
        tabIndex={readOnly ? -1 : undefined}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left text-(length:--font-size-body-medium) leading-(--font-line-height-20)",
            !selectedOption && !renderValue && "text-input-text-placeholder",
          )}
        >
          {renderValue
            ? renderValue(selectedOption)
            : (selectedOption?.label ?? (readOnly ? null : placeholder))}
        </span>
        {!readOnly && <Icon name="chevron-down" size="xs" tone="inherit" />}
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal container={portalHost}>
        {/* Collision detection must measure the menu's minimum width, not only the trigger. */}
        <SelectPrimitive.Positioner
          alignItemWithTrigger={false}
          sideOffset={4}
          className={cn(
            "z-[var(--z-layer-popover)] max-w-[calc(100vw-var(--spacing-16))]",
            popupWidth === "content"
              ? "w-auto min-w-(--anchor-width)"
              : "w-(--anchor-width) min-w-[180px]",
          )}
        >
          <SelectPrimitive.Popup
            className={menuSurfaceClassName({ size: "auto" })}
          >
            <SelectPrimitive.List className="flex flex-col outline-none">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={menuItemClassName({})}
                >
                  {option.icon && (
                    <span className="inline-flex shrink-0 text-menu-icon-default">
                      <Icon name={option.icon} size="xs" tone="inherit" />
                    </span>
                  )}
                  <MenuItemLayout checked={option.value === value}>
                    <SelectPrimitive.ItemText>
                      {renderOption ? renderOption(option) : option.label}
                    </SelectPrimitive.ItemText>
                  </MenuItemLayout>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.List>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
